import React from 'react';
import HarbourIcon, { HarbourGlyph } from './HarbourIcon';
import './harbour.css';

/*
 * Harbour/Status chip. Every status carries colour + glyph + a word — meaning
 * never rides on colour alone. Statuses and default labels come from the
 * component library; pass `label` to use RMS-native copy where it differs.
 */
const CHIP_CONFIG = {
  notStarted: { label: 'Not started', glyph: 'ring-empty', className: 'harbour-chip--not-started' },
  inProgress: { label: 'In progress', glyph: 'ring-half', className: 'harbour-chip--in-progress' },
  done: { label: 'Done', glyph: 'ring-tick', className: null }, // tone decides
  reportAvailable: { label: 'Report available', glyph: 'ring-tick', className: 'harbour-chip--report-available' },
  // Plan-stage end state (Figma TDI-53 section 30): same done-SEN colours as
  // reportAvailable, which it replaces once the SEND support plan is ticked.
  planCreated: { label: 'Plan created', glyph: 'ring-tick', className: 'harbour-chip--report-available' },
  // Do and Review (interim wording pending Tom and Jasmine; the frames leave
  // both as PROPOSED shells): the support in place, then reviewed.
  supportInPlace: { label: 'Support in place', glyph: 'ring-tick', className: 'harbour-chip--report-available' },
  reviewHeld: { label: 'Reviewed', glyph: 'ring-tick', className: 'harbour-chip--report-available' },
  sentWaitingForReview: { label: 'Sent, waiting for review', glyph: 'clock', className: 'harbour-chip--sent-waiting' },
  waitingForSchool: { label: 'Waiting for the school', glyph: 'clock', className: 'harbour-chip--sent-waiting' },
  inReview: { label: 'In review', glyph: 'eye', className: 'harbour-chip--in-review' },
  outcomeAvailable: { label: 'Outcome available', glyph: 'ring-tick', className: 'harbour-chip--outcome-available' },
  stoppedConsentWithdrawn: { label: 'Stopped: consent withdrawn', glyph: 'stop', className: 'harbour-chip--stopped' },
  // The referral's own words (TDI-474). A school reading "stopped" on a
  // referral has to guess who stopped it and why; naming the family answers
  // both, and is what the SENCO needs to know before chasing the parent for
  // forms that will never be used.
  stoppedConsentWithdrawnByFamily: { label: 'Consent withdrawn by the parent or carer', glyph: 'stop', className: 'harbour-chip--stopped' },
  stoppedNoConsent: { label: 'Stopped: no consent', glyph: 'stop', className: 'harbour-chip--stopped' },
  // A not-accepted decision whose letter has been sent reads "Outcome
  // available", exactly as an accepted one does (TDI-826 part 4, T-17):
  // "Closed" made families and schools think something was still to come,
  // and which way the decision went belongs in the letter, not a chip.
  closed: { label: 'Outcome available', glyph: 'ring-tick', className: 'harbour-chip--outcome-available' },
  locked: { label: 'Locked', glyph: null, className: 'harbour-chip--locked' },
};

export const HARBOUR_CHIP_STATUSES = Object.keys(CHIP_CONFIG);

export default function HarbourStatusChip({ status, tone = 'neutral', surface = 'tinted', label, className = '' }) {
  const config = CHIP_CONFIG[status];
  if (!config) return null;

  let statusClass = config.className;
  if (status === 'done') {
    statusClass = tone === 'referral' ? 'harbour-chip--done-referral' : 'harbour-chip--done-sen';
  }

  const classes = [
    'harbour-chip',
    statusClass,
    surface === 'onWhite' ? 'harbour-chip--on-white' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes}>
      {status === 'locked' ? (
        <HarbourIcon name="lock" size={16} />
      ) : (
        <HarbourGlyph kind={config.glyph} size={16} />
      )}
      {label || config.label}
    </span>
  );
}
